import { type AuthConfigDefaultRole, ProvisioningMode, Role } from '@today/types'
import { SelectField } from '@today/ui-components'
import { HelpText, Label, Radio, RadioGroup, Textarea } from '@today/ui-primitives'
import { useId, useState } from 'react'

/** The provisioning part of the authentication config. */
export interface ProvisioningSettings {
	provisioningMode: ProvisioningMode
	allowedEmailDomains: string[]
	defaultRole: AuthConfigDefaultRole | null
}

/**
 * Props for {@link ProvisioningModeSelector}.
 *
 * @property value - The current settings (controlled).
 * @property onChange - Reports the whole changed settings.
 * @property isDisabled - Disables every input.
 */
export interface ProvisioningModeSelectorProps {
	value: ProvisioningSettings
	onChange: (value: ProvisioningSettings) => void
	isDisabled?: boolean
}

const MODES: { mode: ProvisioningMode; label: string; explanation: string }[] = [
	{
		mode: ProvisioningMode.JIT,
		label: 'Just-in-time',
		explanation: 'Create an account on the first successful sign-in through a verified identity provider.',
	},
	{
		mode: ProvisioningMode.INVITE,
		label: 'Invite users directly',
		explanation: 'Nobody gets an account by signing in. An admin invites each person.',
	},
	{
		mode: ProvisioningMode.JIT_WITH_ALLOWLIST,
		label: 'Just-in-time with allowlist',
		explanation: 'Like just-in-time, but only for email addresses in the allowed domains.',
	},
	{
		mode: ProvisioningMode.SCIM,
		label: 'SCIM (available once SCIM ships)',
		explanation: 'Selectable now; until SCIM ships it behaves like inviting users directly.',
	},
]

const ROLE_OPTIONS = [
	{ id: Role.ORG_USER, text: 'Branch user (Gestor de oficina)' },
	{ id: Role.ORG_MANAGER, text: 'Branch manager (Director de oficina)' },
]

/** One domain per line, blank lines dropped. The server trims, lowercases and de-duplicates. */
const parseDomains = (text: string): string[] =>
	text
		.split('\n')
		.map((line) => line.trim())
		.filter((line) => line.length > 0)

const isJit = (mode: ProvisioningMode) => mode === ProvisioningMode.JIT || mode === ProvisioningMode.JIT_WITH_ALLOWLIST

/** The allowlist editor: one domain per line, matched exactly. */
function AllowlistField(props: { initial: string[]; isDisabled: boolean; onChange: (domains: string[]) => void }) {
	const id = useId()
	const [text, setText] = useState(() => props.initial.join('\n'))
	return (
		<div className="flex flex-col gap-1.5">
			<Label htmlFor={id}>Allowed email domains</Label>
			<Textarea
				id={id}
				value={text}
				disabled={props.isDisabled}
				placeholder={'acme.com\neng.acme.com'}
				onChange={(event) => {
					setText(event.target.value)
					props.onChange(parseDomains(event.target.value))
				}}
			/>
			<HelpText>One per line, matched exactly — a subdomain needs its own line.</HelpText>
		</div>
	)
}

/**
 * How a tenant's users come into existence (TO-410 decision 3,
 * jit-provisioning REQ-003): a radio group with an explanation per mode, the
 * allowlist for "just-in-time with allowlist", and the role every
 * just-in-time user receives.
 *
 * Pure and controlled. The role cap and domain rules are the server's; a
 * refusal (`JIT_ROLE_REJECTED`) is shown by the enclosing form.
 */
export function ProvisioningModeSelector({ value, onChange, isDisabled = false }: ProvisioningModeSelectorProps) {
	const update = (patch: Partial<ProvisioningSettings>) => onChange({ ...value, ...patch })
	return (
		<fieldset className="flex flex-col gap-4">
			<legend className="text-ds-base font-semibold text-ds-text">User provisioning</legend>
			<RadioGroup
				aria-label="User provisioning"
				value={value.provisioningMode}
				isDisabled={isDisabled}
				onChange={(mode) => update({ provisioningMode: mode as ProvisioningMode })}
			>
				{MODES.map(({ mode, label, explanation }) => (
					<div key={mode} className="flex flex-col gap-0.5">
						<Radio value={mode}>{label}</Radio>
						<span className="pl-7 text-ds-sm text-ds-text-muted">{explanation}</span>
					</div>
				))}
			</RadioGroup>
			{value.provisioningMode === ProvisioningMode.JIT_WITH_ALLOWLIST ? (
				<AllowlistField
					initial={value.allowedEmailDomains}
					isDisabled={isDisabled}
					onChange={(allowedEmailDomains) => update({ allowedEmailDomains })}
				/>
			) : null}
			{isJit(value.provisioningMode) ? (
				<SelectField
					label="Default role for new users"
					placeholder="Select a role"
					options={ROLE_OPTIONS}
					value={value.defaultRole ?? ''}
					onValueChange={(role) => update({ defaultRole: role as AuthConfigDefaultRole })}
				/>
			) : null}
		</fieldset>
	)
}
